/**
 * Reading a count sheet — the two questions the screen must not get wrong.
 *
 * IN `lib/` AND NOT IN StockTake.jsx for the same reason lib/views.js is: node
 * cannot parse JSX, so anything that lives beside a component can only ever be
 * checked by eye. These two rules are the flagship invariants of the whole
 * count screen, and "checked by eye" is not good enough for either.
 */

/**
 * Does this line carry the expected figure?
 *
 * THE KEY'S PRESENCE, NEVER ITS VALUE. A holder of `stock.count` without
 * `stock.view` — precisely a `client_user`, the person who actually walks the
 * shelves — gets lines where `expectedQty` is NOT A KEY AT ALL. Not null, not
 * zero: absent. projectTakeLine builds the object without it rather than
 * stripping it afterwards, because a field removed by the browser is a field
 * that was on the wire.
 *
 * So the question is `hasOwnProperty` and nothing else:
 *
 *  - `line.expectedQty != null` would read a GENUINE expected zero — a line the
 *    books say holds nothing — as a blind line, and hide the column from a
 *    reviewer entitled to it.
 *  - `line.expectedQty ?? 0` would invent an expected figure for somebody the
 *    API went to trouble to withhold one from, which is the exact hazard the
 *    two separate capabilities exist to prevent. A counter who can see the
 *    expected figure counts to it, and the number they write down is the number
 *    that was already there.
 */
export const hasExpected = (line) =>
  Object.prototype.hasOwnProperty.call(line, 'expectedQty');

/**
 * Does this RESPONSE carry the expected figures — i.e. should the sheet render
 * the expected and difference columns at all?
 *
 * `some` rather than "the first line", so an empty sheet is treated as blind.
 * Blind is the safe default in both directions: the columns are absent when
 * nothing says they should be there, rather than present and empty.
 */
export const showsExpected = (lines) => (lines ?? []).some(hasExpected);

/**
 * What one line READS today: the count held on this device if there is one,
 * otherwise the count the server has.
 *
 * The local answer wins because the counter keyed it and has not yet had signal
 * — a count they cannot see is a count they will key again, into a queue that
 * will then have to supersede it.
 *
 * A REFUSED COUNT IS NOT AN ANSWER. If the queued record was permanently
 * rejected, the line's answer is whatever the SERVER holds — which may be an
 * earlier accepted count, or nothing at all — and the refused figure comes back
 * separately as `refused` so the screen can mark it as the problem it is.
 * Returning it as `qty` would render a figure the books never took identically
 * to one they did, count it as answered, and hide the line behind the "not
 * counted" filter — which is the counter's only tool for finding what is left.
 *
 * NULL STAYS NULL. `?? 0` here would tell a counter their blank line was
 * answered, and a blank line is the one thing in this module that must never
 * read as zero: a zero says "we counted and there were none", a blank says
 * "nobody has reached this yet", and posting treats them completely differently.
 */
export function answerFor(line, pending, keyOf) {
  const local = pending?.get(keyOf(line));
  if (local && !local.failed) {
    return { qty: local.countedQty, local: true, refused: null };
  }
  return {
    qty: line.countedQty ?? null,
    local: false,
    refused: local?.failed ? local.countedQty : null,
  };
}

/**
 * True when nobody has answered this line yet — on the server, and not counting
 * a figure the server refused.
 */
export const isUncounted = (line, pending, keyOf) => {
  const { qty } = answerFor(line, pending, keyOf);
  return qty === null || qty === undefined;
};

/** How many of these lines have an answer the books would actually take. */
export const countedCount = (lines, pending, keyOf) =>
  (lines ?? []).filter((l) => !isUncounted(l, pending, keyOf)).length;
